import React, { useState } from 'react'
import { AppIcons } from '../icons'

interface LoginFormProps {
  onLogin: (email: string, password: string) => Promise<{ error: any }>
  onSwitchToSignup: () => void
  onForgotPassword: (email: string) => Promise<{ error: any }>
  loading: boolean
  error: string | null
}

export function LoginForm({ onLogin, onSwitchToSignup, onForgotPassword, loading, error }: LoginFormProps) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showForgotPassword, setShowForgotPassword] = useState(false)
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('')
  const [forgotPasswordSent, setForgotPasswordSent] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email || !password) return
    
    await onLogin(email, password)
  }

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!forgotPasswordEmail) return
    
    const { error } = await onForgotPassword(forgotPasswordEmail)
    if (!error) {
      setForgotPasswordSent(true)
    }
  }

  if (showForgotPassword) {
    return (
      <div className="bg-white p-4 sm:p-6 lg:p-8 rounded-lg shadow-md w-full">
        <div className="flex items-center mb-6">
          <button
            onClick={() => setShowForgotPassword(false)}
            className="mr-3 p-1 hover:bg-gray-100 rounded"
          >
            <AppIcons.ArrowLeft className="w-5 h-5" />
          </button>
          <h2 className="text-2xl font-bold">Reset Password</h2>
        </div>

        {forgotPasswordSent ? (
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <AppIcons.Mail className="w-12 h-12 text-green-500" />
            </div>
            <p className="text-gray-600 mb-4">
              Password reset instructions have been sent to your email address.
            </p>
            <button
              onClick={() => {
                setShowForgotPassword(false)
                setForgotPasswordSent(false)
                setForgotPasswordEmail('')
              }}
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              Back to login
            </button>
          </div>
        ) : (
          <form onSubmit={handleForgotPassword}>
            <div className="mb-4">
              <label htmlFor="reset-email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                id="reset-email"
                type="email"
                value={forgotPasswordEmail}
                onChange={(e) => setForgotPasswordEmail(e.target.value)}
                className="w-full px-3 py-3 lg:py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
                style={{ minHeight: '44px' }}
                placeholder="Enter your email address"
                autoComplete="email"
                inputMode="email"
                required
              />
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-center">
                <AppIcons.AlertTriangle className="w-4 h-4 text-red-500 mr-2" />
                <span className="text-red-700 text-sm">{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !forgotPasswordEmail}
              className="w-full bg-blue-600 text-white py-3 lg:py-2 px-4 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-base"
              style={{ minHeight: '44px' }}
            >
              {loading ? 'Sending...' : 'Send Reset Instructions'}
            </button>
          </form>
        )}
      </div>
    )
  }

  return (
    <div className="bg-white p-4 sm:p-6 lg:p-8 rounded-lg shadow-md w-full">
      <div className="text-center mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Welcome Back</h1>
        <p className="text-sm sm:text-base text-gray-600">Sign in to your StockTagger.ai account</p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
            Email Address
          </label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-3 py-3 lg:py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
            style={{ minHeight: '44px' }}
            placeholder="you@example.com"
            autoComplete="email"
            inputMode="email"
            required
          />
        </div>

        <div className="mb-6">
          <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
            Password
          </label>
          <input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-3 py-3 lg:py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
            style={{ minHeight: '44px' }}
            placeholder="Enter your password"
            autoComplete="current-password"
            required
          />
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-center">
            <AppIcons.AlertTriangle className="w-4 h-4 text-red-500 mr-2" />
            <span className="text-red-700 text-sm">{error}</span>
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-blue-600 text-white py-3 lg:py-2 px-4 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed mb-4 font-medium text-base"
          style={{ minHeight: '44px' }}
        >
          {loading ? 'Signing in...' : 'Sign In'}
        </button>

        <div className="text-center">
          <button
            type="button"
            onClick={() => setShowForgotPassword(true)}
            className="text-sm text-blue-600 hover:text-blue-700 mb-4 block w-full py-2 px-2 rounded-lg hover:bg-blue-50 transition-colors"
            style={{ minHeight: '44px' }}
          >
            Forgot your password?
          </button>
          <span className="text-sm text-gray-600">
            Don't have an account?{' '}
            <button
              type="button"
              onClick={onSwitchToSignup}
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              Sign up
            </button>
          </span>
        </div>
      </form>
    </div>
  )
}